// =====================================================================
// Catálogo único de steps do onboarding (admin + agent).
// Mantido em sync com:
//  - Backend: FieldService.Business.Consts.OnboardingStepCodes (C#)
//  - Backend: Mexx.BackgroundTasks.IntegrationEvents.EventHandling.Onboarding.OnboardingProgressHandler
// Plug novos steps SEMPRE neste arquivo + atualizar backend.
// =====================================================================

export const STEP_IDS = {
    CREATE_FIRST_TICKET: 'create-first-ticket',
    ANSWER_AND_CLOSE_TICKET: 'answer-and-close-ticket',
    CREATE_AGENT_USER: 'create-agent-user',
    CREATE_ORGANIZATION: 'create-organization',
    CREATE_CATEGORY: 'create-category',
    CREATE_TEAM: 'create-team',
    LINK_TEAM_MEMBERS: 'link-team-members',
    LEARN_AUTOMATION_TEMPLATES: 'learn-automation-templates',
};

export const AUDIENCES = {
    ADMIN: 'admin',
    AGENT: 'agent',
    BOTH: 'both',
};

// Configuração rica por step (UI + navegação).
// labelKey / descriptionKey apontam para chaves do _i18n/translations.json.
// Componentes resolvem via intl.formatMessage({ id: labelKey }).
export const STEP_CATALOG = [
    {
        id: STEP_IDS.CREATE_FIRST_TICKET,
        audience: AUDIENCES.BOTH,
        order: 1,
        icon: 'AddCircleOutline',
        labelKey: 'onboarding.step.createFirstTicket.label',
        descriptionKey: 'onboarding.step.createFirstTicket.description',
        navigateTo: '/pt/called-registration',
        tourId: null, // detectado via outbox (called.created)
    },
    {
        id: STEP_IDS.ANSWER_AND_CLOSE_TICKET,
        audience: AUDIENCES.BOTH,
        order: 2,
        icon: 'CheckCircleOutline',
        labelKey: 'onboarding.step.answerAndCloseTicket.label',
        descriptionKey: 'onboarding.step.answerAndCloseTicket.description',
        navigateTo: '/pt/called-list',
        tourId: null,
    },
    {
        id: STEP_IDS.CREATE_AGENT_USER,
        audience: AUDIENCES.ADMIN,
        order: 3,
        icon: 'PersonAddOutlined',
        labelKey: 'onboarding.step.createAgentUser.label',
        descriptionKey: 'onboarding.step.createAgentUser.description',
        navigateTo: '/pt/user-registration',
        tourId: null,
    },
    {
        id: STEP_IDS.CREATE_ORGANIZATION,
        audience: AUDIENCES.ADMIN,
        order: 4,
        icon: 'BusinessOutlined',
        labelKey: 'onboarding.step.createOrganization.label',
        descriptionKey: 'onboarding.step.createOrganization.description',
        navigateTo: '/pt/organization-registration',
        tourId: null,
    },
    {
        id: STEP_IDS.CREATE_CATEGORY,
        audience: AUDIENCES.ADMIN,
        order: 5,
        icon: 'CategoryOutlined',
        labelKey: 'onboarding.step.createCategory.label',
        descriptionKey: 'onboarding.step.createCategory.description',
        navigateTo: '/pt/category-registration',
        tourId: null,
    },
    {
        id: STEP_IDS.CREATE_TEAM,
        audience: AUDIENCES.ADMIN,
        order: 6,
        icon: 'GroupOutlined',
        labelKey: 'onboarding.step.createTeam.label',
        descriptionKey: 'onboarding.step.createTeam.description',
        navigateTo: '/pt/techinicalteam-registration',
        tourId: null,
    },
    {
        id: STEP_IDS.LINK_TEAM_MEMBERS,
        audience: AUDIENCES.ADMIN,
        order: 7,
        icon: 'GroupWorkOutlined',
        labelKey: 'onboarding.step.linkTeamMembers.label',
        descriptionKey: 'onboarding.step.linkTeamMembers.description',
        navigateTo: '/pt/techinicalteamuser-list',
        tourId: null,
    },
    {
        id: STEP_IDS.LEARN_AUTOMATION_TEMPLATES,
        audience: AUDIENCES.ADMIN,
        order: 8,
        icon: 'BoltOutlined',
        labelKey: 'onboarding.step.learnAutomationTemplates.label',
        descriptionKey: 'onboarding.step.learnAutomationTemplates.description',
        navigateTo: '/pt/automation-templates',
        tourId: 'learn-automation-templates',
    },
];

export const TOTAL_STEPS_BY_AUDIENCE = {
    admin: STEP_CATALOG.filter((s) => s.audience === AUDIENCES.ADMIN || s.audience === AUDIENCES.BOTH).length,
    agent: STEP_CATALOG.filter((s) => s.audience === AUDIENCES.AGENT || s.audience === AUDIENCES.BOTH).length,
};

// =====================================================================
// Detecção: usuário está em plano TRIAL?
//
// IMPORTANTE — gate principal. Componentes de onboarding só devem renderizar
// quando isUserOnTrial(user) === true. Hirers pagos (dedicated/standard)
// NUNCA devem ver welcome modal nem checklist.
//
// Convenção: contract_status_id = 5 = "Em Trial" (catálogo SaaS).
// O JWT/login payload do projeto carrega isso. Como JSON é all-lowercase
// (LowercaseContractResolver no backend), o nome esperado é "contractstatusid".
// Defensivo: aceita também variantes camelCase / snake_case se algum payload
// novo escapar a convenção.
// =====================================================================
const CONTRACT_STATUS_TRIAL = 5;

export function isUserOnTrial(user) {
    if (!user) return false;
    const status =
        user.contractstatusid ??
        user.contract_status_id ??
        user.contractStatusId ??
        null;
    return status === CONTRACT_STATUS_TRIAL;
}

// =====================================================================
// Detecção de audience baseada no userSession.user.userrole
// (lista de roles que o user possui — projeto retorna como array de objetos)
// Roles por id:
//   1 = Administrador Sistema
//   2 = Administrador de Organizações
//   3 = Coordenador de Organizações
//   4 = Cliente
//   5 = Técnico
// =====================================================================
const ADMIN_ROLE_IDS = new Set([1, 2, 3]);
const AGENT_ROLE_IDS = new Set([5]);

export function detectAudience(user) {
    if (!user) return AUDIENCES.ADMIN;

    const roles = user.userrole ?? user.userRole ?? user.userroles ?? [];
    if (!Array.isArray(roles) || roles.length === 0) return AUDIENCES.ADMIN;

    const ids = roles
        .map((r) => (typeof r === 'object' ? r.roleid ?? r.roleId ?? r.role_id : Number(r)))
        .filter((id) => Number.isFinite(id));

    const isAdmin = ids.some((id) => ADMIN_ROLE_IDS.has(id));
    if (isAdmin) return AUDIENCES.ADMIN;

    const isAgent = ids.some((id) => AGENT_ROLE_IDS.has(id));
    if (isAgent) return AUDIENCES.AGENT;

    return AUDIENCES.ADMIN;
}

export function filterStepsByAudience(audience) {
    if (audience === AUDIENCES.ADMIN) {
        return STEP_CATALOG.filter(
            (s) => s.audience === AUDIENCES.ADMIN || s.audience === AUDIENCES.BOTH
        );
    }
    if (audience === AUDIENCES.AGENT) {
        return STEP_CATALOG.filter(
            (s) => s.audience === AUDIENCES.AGENT || s.audience === AUDIENCES.BOTH
        );
    }
    return STEP_CATALOG;
}

// =====================================================================
// Tour de templates de automação — único tour interativo no V1
// Outros steps são "navegação + ação" (não precisam de Joyride)
// =====================================================================
export const TOUR_LEARN_AUTOMATION_TEMPLATES = {
    id: 'learn-automation-templates',
    audience: AUDIENCES.ADMIN,
    steps: [
        {
            target: 'body',
            placement: 'center',
            content:
                'Templates de automação economizam horas do seu time. Eles configuram regras prontas como "atribuir chamados de e-mail ao agente X" ou "notificar gestor quando SLA estourar".',
            disableBeacon: true,
        },
        {
            target: '[data-tour="automation-templates-list"]',
            placement: 'auto',
            content:
                'Aqui ficam os templates disponíveis. Você ativa qualquer um com um clique e pode customizar depois.',
            disableBeacon: true,
        },
        {
            target: 'body',
            placement: 'center',
            content:
                'Pronto! Você já sabe onde encontrar e ativar automações. Marcamos esse passo como concluído.',
            disableBeacon: true,
        },
    ],
};
